// Settings → Inbound email tab. Three sections:
//   1. The org's dedicated inbound address — large copy chip.
//   2. Sub-address aliases — additional +slug values that route to
//      the same workspace (sub-brands, M&A, migrations).
//   3. Recent inbound deliveries — log table with color-coded
//      resolution badges and links to the resulting request.

import { useState } from 'react'
import {
  Box, Button, Card, Chip, CircularProgress, Divider, IconButton,
  Stack, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded'
import { ApiError, type InboundResolutionMethod } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

const RESOLUTION_META: Record<InboundResolutionMethod, { label: string; color: string; bg: string }> = {
  sub_address:       { label: 'Sub-address',       color: '#047857', bg: '#D1FAE5' },
  sub_address_alias: { label: 'Sub-address alias', color: '#0E7490', bg: '#CFFAFE' },
  reply:             { label: 'Reply',             color: '#4338CA', bg: '#E0E7FF' },
  bounced:           { label: 'Bounced',           color: '#B45309', bg: '#FEF3C7' },
  error:             { label: 'Error',             color: '#B91C1C', bg: '#FEE2E2' },
}

export function InboundEmailTab() {
  const qc = useQueryClient()
  const configQ  = useQuery({ queryKey: ['inbound-email', 'config'],  queryFn: () => api.inboundEmail.getConfig() })
  const aliasesQ = useQuery({ queryKey: ['inbound-email', 'aliases'], queryFn: () => api.inboundEmail.listAliases() })
  const logQ     = useQuery({ queryKey: ['inbound-email', 'log'],     queryFn: () => api.inboundEmail.listLog(50) })

  const [newAlias, setNewAlias] = useState('')

  const addAliasM = useMutation({
    mutationFn: (a: string) => api.inboundEmail.addAlias(a),
    onSuccess: () => {
      toast.success('Alias added')
      setNewAlias('')
      qc.invalidateQueries({ queryKey: ['inbound-email', 'aliases'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to add alias'),
  })
  const removeAliasM = useMutation({
    mutationFn: (id: string) => api.inboundEmail.removeAlias(id),
    onSuccess: () => {
      toast.success('Alias removed')
      qc.invalidateQueries({ queryKey: ['inbound-email', 'aliases'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to remove alias'),
  })

  const cfg = configQ.data
  const aliases = aliasesQ.data ?? []
  const logs = logQ.data ?? []

  function copyAddress() {
    if (!cfg?.inbound_address) return
    navigator.clipboard.writeText(cfg.inbound_address).then(() => toast.success('Address copied'))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── Address card ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Box sx={{
          px: 3, py: 2.25,
          background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.1)} 0%, ${alpha(t.palette.primary.main, 0.03)} 100%)`,
          borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
              color: 'primary.contrastText',
            }}>
              <EmailOutlinedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
                textTransform: 'uppercase', color: 'primary.main', lineHeight: 1.1 }}>
                Your inbound address
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3 }}>
                Email-to-request
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Box sx={{ p: 3 }}>
          {configQ.isLoading ? (
            <CircularProgress size={22} />
          ) : !cfg ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
              Couldn't load inbound configuration.
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
                Customers can email this address to open a request automatically.
                Share it with your team and your customers — replies are threaded
                back to the same request.
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}
                sx={{
                  p: 1.25, borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                  border: '1px dashed', borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                }}>
                <Typography sx={{
                  flex: 1, fontFamily: 'monospace',
                  fontSize: 14, fontWeight: 700, color: 'primary.dark',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {cfg.inbound_address}
                </Typography>
                <Tooltip title="Copy address" arrow>
                  <IconButton size="small" onClick={copyAddress}
                    sx={{ color: 'primary.main' }}>
                    <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </>
          )}
        </Box>
      </Card>

      {/* ── Sub-address aliases ─────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AlternateEmailRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Sub-address aliases</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>
            Additional <code>+slug</code> values that route to this workspace too.
            Use for sub-brands or legacy/migrated org names — both
            <Box component="code" sx={{ mx: 0.5, px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: 'action.hover' }}>
              {cfg?.inbound_local_part ?? 'support'}+{cfg?.org_slug ?? 'org'}@{cfg?.inbound_domain ?? 'bacsys.net'}
            </Box>
            and any alias you add will land in the same Requests page.
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
            <TextField
              size="small" placeholder="org_alias"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newAlias.trim() && !addAliasM.isPending) {
                  addAliasM.mutate(newAlias.trim())
                }
              }}
              sx={{ flex: 1, maxWidth: 200,
                '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
              InputProps={{
                endAdornment: cfg ? (
                  <Box component="span" sx={{
                    fontSize: 11.5, color: 'text.disabled', whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                  }}>
                    @{cfg.inbound_domain}
                  </Box>
                ) : undefined,
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
              disabled={!newAlias.trim() || addAliasM.isPending}
              onClick={() => addAliasM.mutate(newAlias.trim())}
              sx={{ fontSize: 13, borderRadius: 2 }}
            >
              Add
            </Button>
          </Stack>

          {aliasesQ.isLoading ? (
            <CircularProgress size={20} />
          ) : aliases.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>
              No extra aliases yet. The canonical address{' '}
              <code>{cfg?.inbound_local_part ?? 'support'}+{cfg?.org_slug ?? 'org'}@{cfg?.inbound_domain ?? 'bacsys.net'}</code>
              {' '}always works.
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {aliases.map((a) => (
                <Chip
                  key={a.id}
                  label={
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                      {cfg?.inbound_local_part ?? 'support'}+
                      <Box component="span" sx={{ fontWeight: 800, color: 'primary.dark' }}>
                        {a.alias}
                      </Box>
                      @{cfg?.inbound_domain ?? 'bacsys.net'}
                    </Box>
                  }
                  variant="outlined"
                  onDelete={() => removeAliasM.mutate(a.id)}
                  deleteIcon={<DeleteOutlineRoundedIcon />}
                  sx={{
                    height: 30, fontSize: 12, fontWeight: 500,
                    bgcolor: 'background.paper',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Card>

      {/* ── Inbound log ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}
          sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Recent inbound emails</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
              Live audit trail of every message Postal forwarded for your org.
            </Typography>
          </Box>
          <Tooltip title="Refresh" arrow>
            <IconButton size="small"
              onClick={() => qc.invalidateQueries({ queryKey: ['inbound-email', 'log'] })}>
              <RefreshRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box sx={{ p: logs.length === 0 ? 4 : 0 }}>
          {logQ.isLoading ? (
            <CircularProgress size={20} />
          ) : logs.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', fontStyle: 'italic' }}>
              Nothing here yet. Try sending an email to your inbound address.
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {logs.map((row) => {
                const meta = row.resolution_method ? RESOLUTION_META[row.resolution_method] : null
                const requestId = row.created_request_id ?? row.thread_request_id
                return (
                  <Stack key={row.id} direction="row" spacing={2} alignItems="center"
                    sx={{ px: 3, py: 1.5 }}>
                    {/* status icon */}
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: meta?.bg ?? '#F1F5F9', color: meta?.color ?? '#475569',
                    }}>
                      {row.resolution_method === 'error' || row.resolution_method === 'bounced'
                        ? <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />
                        : <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                    </Box>
                    {/* content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          flex: 1, minWidth: 0 }}>
                          {row.subject || '(no subject)'}
                        </Typography>
                        {meta && (
                          <Chip size="small" label={meta.label}
                            sx={{
                              height: 18, fontSize: 10, fontWeight: 800,
                              bgcolor: meta.bg, color: meta.color,
                              '& .MuiChip-label': { px: 0.85 },
                            }} />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                        From <strong>{row.from_address ?? 'unknown'}</strong> ·{' '}
                        {new Date(row.received_at).toLocaleString()}
                        {row.error && (
                          <Typography component="span" sx={{ ml: 0.75, color: 'error.main', fontSize: 11.5 }}>
                            — {row.error}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    {requestId && (
                      <Tooltip title="Open request" arrow>
                        <IconButton component={RouterLink} to={`/requests`} size="small"
                          sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                          <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Box>
      </Card>
    </Box>
  )
}
